/**
 * Dynamic Questions Hook (V2 - Performance Optimized)
 *
 * Fetches personalized starter questions from the backend.
 * - Returns cached questions immediately for instant display
 * - Fetches fresh questions in the background
 * - Caches questions per plan in sessionStorage
 * - Falls back to static questions if fetch fails
 *
 * The backend uses Claude to generate context-aware questions based on the
 * user's onboarding state, career goals, requirements progress, and plan status.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { typedApi } from '../services/typedApiClient';

const getCacheKey = (planId: number | string): string => `dynamic_questions_${planId}`;

const FALLBACK_QUESTIONS: string[] = [
  'What courses should I take next semester?',
  'How am I progressing on my requirements?',
  'What electives would you recommend?',
];

const CACHE_EXPIRY_MS = 30 * 60 * 1000;

interface CachedEntry {
  questions: string[];
  state: string | null;
  timestamp: number;
}

interface CachedQuestions {
  questions: string[];
  state: string | null;
}

const getCachedQuestions = (planId: number | string): CachedQuestions | null => {
  try {
    const cached = sessionStorage.getItem(getCacheKey(planId));
    if (cached) {
      const { questions, state, timestamp } = JSON.parse(cached) as CachedEntry;
      if (Date.now() - timestamp < CACHE_EXPIRY_MS) {
        return { questions, state };
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
};

const setCachedQuestions = (
  planId: number | string,
  questions: string[],
  state: string | null
): void => {
  try {
    const entry: CachedEntry = { questions, state, timestamp: Date.now() };
    sessionStorage.setItem(getCacheKey(planId), JSON.stringify(entry));
  } catch {
    // Ignore cache errors
  }
};

export interface UseDynamicQuestionsReturn {
  questions: string[];
  loading: boolean;
  error: string | null;
  state: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching dynamic starter questions.
 *
 * @param academicPlanId The academic plan ID
 * @param enabled Whether to fetch questions (default true)
 */
export const useDynamicQuestions = (
  academicPlanId: number | string | null | undefined,
  enabled = true
): UseDynamicQuestionsReturn => {
  const [questions, setQuestions] = useState<string[]>(() => {
    if (!academicPlanId) return FALLBACK_QUESTIONS;
    const cached = getCachedQuestions(academicPlanId);
    return cached?.questions || FALLBACK_QUESTIONS;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(() => {
    if (!academicPlanId) return null;
    const cached = getCachedQuestions(academicPlanId);
    return cached?.state || null;
  });

  const fetchedPlanRef = useRef<number | string | null>(null);

  const fetchQuestions = useCallback(async (): Promise<void> => {
    if (!academicPlanId || !enabled) {
      return;
    }

    // Skip fetch if we already have cached questions for this exact plan
    // and we've already fetched in this session.
    if (fetchedPlanRef.current === academicPlanId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await typedApi.chat.getStarterQuestions({
        academic_plan_id: Number(academicPlanId),
      });

      if (data && data.questions && data.questions.length > 0) {
        const fetchedQuestions = data.questions;
        const fetchedState = data.state ?? null;
        setQuestions(fetchedQuestions);
        setState(fetchedState);
        setCachedQuestions(academicPlanId, fetchedQuestions, fetchedState);
        fetchedPlanRef.current = academicPlanId;
      }
      // If no questions returned, keep showing current questions (cached or fallback).
    } catch (err) {
      console.error('Failed to fetch dynamic questions:', err);
      setError((err as { message?: string })?.message || 'Failed to load questions');
      // Keep showing current questions on error (don't clear).
    } finally {
      setLoading(false);
    }
  }, [academicPlanId, enabled]);

  // Load cached questions immediately when plan changes.
  useEffect(() => {
    if (academicPlanId) {
      const cached = getCachedQuestions(academicPlanId);
      if (cached?.questions) {
        setQuestions(cached.questions);
        setState(cached.state);
      } else {
        setQuestions(FALLBACK_QUESTIONS);
      }
    }
  }, [academicPlanId]);

  // Fetch fresh questions in background.
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return {
    questions,
    loading,
    error,
    state,
    refetch: fetchQuestions,
  };
};

export default useDynamicQuestions;
